import { useState, useEffect } from 'react';
import { userApi } from '../../api/user.api';
import { useAuth } from '../../store/AuthContext';
import { Avatar, Badge, EmptyState, PageHeader, Skeleton } from '../../components/ui/index';
import { toPersian } from '../../utils/persianNumber';
import { useDebounce } from '../../hooks/useDraft';
import UserActionsModal from '../../components/users/UserActionsModal';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { User } from '@phosphor-icons/react';

const FILTERS = ['همه', 'فعال', 'غیرفعال'];

export default function UserListPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('همه');
  const [search, setSearch] = useState('');
  const [actionsTarget, setActionsTarget] = useState(null);
  const q = useDebounce(search, 300);
  // change_user_password/change_user_activation/admin_edit_phone_number are
  // all manager_employee/dispatcher-only on the backend - manager_user
  // browsing their own company's users has no such power, so the row
  // click-to-manage is only offered to the roles that can actually use it.
  const canManage = user?.type === 'dispatcher' || user?.type === 'manager_employee';

  const load = (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    // manager_user can only see their own company's users (get_user_list_view
    // is dispatcher/manager_employee only and would 403 for them); everyone
    // else keeps seeing the full cross-company list as before.
    const fetch = user?.type === 'manager_user' ? userApi.getCompanyUserList() : userApi.getUserListView();
    fetch
      .then((res) => setUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, [user?.type]);
  useAutoRefresh(() => load(false), { paused: !!actionsTarget });

  const filtered = users.filter((u) => {
    const name = u.username || `${u.first_name || ''} ${u.last_name || ''}`.trim();
    const m = !q || name.includes(q);
    if (filter === 'فعال') return m && !!u.active;
    if (filter === 'غیرفعال') return m && !u.active;
    return m;
  });

  return (
    <div className="fade-in">
      <PageHeader title="لیست کاربران" subtitle={`${toPersian(filtered.length)} کاربر`} />
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} style={{ padding: '7px 16px', borderRadius: 20, border: `1.5px solid ${filter === f ? '#29AAE1' : '#e2e8f0'}`, background: filter === f ? '#EDF3FC' : '#fff', color: filter === f ? '#233A7A' : '#64748b', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>{f}</button>
        ))}
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="جستجو..." style={{ marginRight: 'auto', padding: '7px 14px', border: '1.5px solid #e2e8f0', borderRadius: 20, fontSize: 12, outline: 'none', fontFamily: 'inherit', width: 200 }} />
      </div>

      {loading
        ? <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[1,2,3,4].map((k) => <div key={k} style={{ background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0', display: 'flex', gap: 12, alignItems: 'center' }}><div style={{ width: 40, height: 40, borderRadius: '50%', background: '#f1f5f9' }} /><div style={{ flex: 1 }}><Skeleton h={16} w="40%" style={{ marginBottom: 6 }} /><Skeleton h={12} w="60%" /></div></div>)}</div>
        : filtered.length === 0
          ? <EmptyState icon={<User size={44} weight="regular" />} title="کاربری یافت نشد" />
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filtered.map((u, i) => (
                <div key={u.id || i} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.15s', cursor: canManage ? 'pointer' : 'default' }}
                  onClick={() => canManage && setActionsTarget({ id: u.id, username: u.username, active: u.active, type: 'user' })}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#B7C9EC'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                >
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <Avatar name={u.username || '؟'} size={42} />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{u.username}</div>
                      {(u.first_name || u.last_name) && <div style={{ fontSize: 12, color: '#94a3b8' }}>{[u.first_name, u.last_name].filter(Boolean).join(' ')}</div>}
                      {u.company_name && <div style={{ fontSize: 12, color: '#94a3b8' }}>{u.company_display_name || u.company_name}</div>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Badge variant={u.active ? 'green' : 'gray'}>{u.active ? 'فعال' : 'غیرفعال'}</Badge>
                    {u.is_manager && <Badge variant="blue">مدیر</Badge>}
                  </div>
                </div>
              ))}
            </div>
          )
      }
      <UserActionsModal target={actionsTarget} onClose={() => setActionsTarget(null)} onChanged={load} />
    </div>
  );
}
